import CirclesMultiSelect from '@/circle/components/CirclesMultiSelect'
import MembersMultiSelect from '@/member/components/MembersMultiSelect'
import MemberSearchButton from '@/search/components/MemberSearchButton'
import {
  Button,
  FormControl,
  FormLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CreateIcon, NoteIcon } from 'src/icons'
import { stepsAndIcons } from './stepTypes'

interface Props {
  circlesIds: string[]
  membersIds: string[]
  excludeMemberId?: string
  // Chosen people without an account, who will get no email
  withoutAccountNames: string[]
  // The steps of the compte rendu, and those left out of the share
  steps: { key: string; type: string; title: string }[]
  excludedSteps: string[]
  onChangeCircles(circlesIds: string[]): void
  onChangeMembers(membersIds: string[]): void
  onToggleStep(key: string): void
}

// Step 1: to whom, and which steps of the meeting. Members of the organization
// only; choosing a circle adds its people to the list, as when inviting a
// circle to a meeting (grilling of 26/09/2026)
export default function MeetingSummaryShareRecipients({
  circlesIds,
  membersIds,
  excludeMemberId,
  withoutAccountNames,
  steps,
  excludedSteps,
  onChangeCircles,
  onChangeMembers,
  onToggleStep,
}: Props) {
  const { t } = useTranslation()

  return (
    <VStack spacing={5} align="stretch">
      <FormControl>
        <FormLabel>{t('MeetingSummaryShareModal.circles')}</FormLabel>
        <CirclesMultiSelect
          circlesIds={circlesIds}
          onChange={onChangeCircles}
        />
      </FormControl>

      <FormControl>
        <FormLabel>
          {t('MeetingSummaryShareModal.recipients', {
            count: membersIds.length,
          })}
        </FormLabel>
        {membersIds.length === 0 && (
          <Text color="gray.500" _dark={{ color: 'gray.300' }} mb={2}>
            {t('MeetingSummaryShareModal.nobody')}
          </Text>
        )}
        {/* Adding someone comes first, above the people the circles brought;
            the search leaves out whoever is already listed, so nobody is
            listed twice */}
        <MemberSearchButton
          excludeIds={
            excludeMemberId ? [...membersIds, excludeMemberId] : membersIds
          }
          size="sm"
          variant="outline"
          leftIcon={<CreateIcon size={20} />}
          mb={3}
          onSelect={(id) => onChangeMembers([...membersIds, id])}
        >
          {t('MembersMultiSelect.add')}
        </MemberSearchButton>
        <MembersMultiSelect
          membersIds={membersIds}
          onRemove={(id) => onChangeMembers(membersIds.filter((m) => m !== id))}
        />
        {withoutAccountNames.length > 0 && (
          <Text
            mt={3}
            fontSize="sm"
            color="orange.600"
            _dark={{ color: 'orange.300' }}
          >
            {t('MeetingSummaryShareModal.noEmailFor', {
              names: withoutAccountNames.join(', '),
            })}
          </Text>
        )}
      </FormControl>

      {steps.length > 0 && (
        <FormControl>
          <FormLabel>{t('MeetingSummaryShareModal.steps.title')}</FormLabel>
          <Wrap spacing={2}>
            {steps.map((step) => {
              const selected = !excludedSteps.includes(step.key)
              const Icon =
                stepsAndIcons.find((s) => s.type === step.type)?.icon ??
                NoteIcon
              return (
                <WrapItem key={step.key}>
                  <Button
                    size="sm"
                    variant={selected ? 'solid' : 'outline'}
                    colorScheme={selected ? 'blue' : undefined}
                    opacity={selected ? 1 : 0.6}
                    leftIcon={<Icon size={16} />}
                    aria-pressed={selected}
                    onClick={() => onToggleStep(step.key)}
                  >
                    {step.title}
                  </Button>
                </WrapItem>
              )
            })}
          </Wrap>
        </FormControl>
      )}
    </VStack>
  )
}
